import type { Price, Sku } from '@commercelayer/sdk'
import type { AlgoliaProductType } from '@integration-layer/modules/05.productServices/runtime/components/WrapperProductTile.props'
import type {
  extColor,
  ColorOption,
} from '@design-system/components/Pdp/PdpColorSelector.props'
import type { SizeData } from '@design-system/components/Pdp/PdpFitFinder.props'
import type { ProductCareItemList } from '@design-system/components/Pdp/EcommerceBlock/PdpEcommerceBlockProductInfo.props'
import type { MainCategory } from '@integration-layer/components/Templates/TemplatesPdp.props'
import { getLangCountryMap } from '@integration-layer/integrationConfig'
import { countries } from '@design-system/configs/countries'
import type { PdpEcommerceBlockProductFocusSideSlideProps } from '@design-system/components/Pdp/EcommerceBlock/PdpEcommerceBlockProductFocusSideSlide.props'
import { extractCategoriesUnlocalized } from '@design-system/utils/formatAlgoliaHierarchicalCategories'
import type { PdpEcommerceBlockProductPdpMessageSideSlideProps } from '@design-system/components/Pdp/EcommerceBlock/PdpEcommerceBlockProductPdpMessageSideSlide.props'

export type AlgoliaSize = AlgoliaProductType['size']

/**
 * Type definition for the mapped product data used in the Product Detail Page (PDP).
 * Represents the structured data of a product after being processed and mapped from raw data.
 *
 * @typedef {Object} PDPMappedProductData
 * @property {string} title - The product title.
 * @property {number} price - The current price of the product.
 * @property {number} [priceCents] - The price in cents, if applicable.
 * @property {number} amountFloat - The price as a floating point number.
 * @property {string} currencyCode - The currency code for the product price (e.g., USD, EUR).
 * @property {number} [oldPrice] - The original price before discount, if applicable.
 * @property {string} [discount] - The discount information, if applicable.
 * @property {string[]} [tags] - Array of tags associated with the product.
 * @property {boolean} [isRetailExclusive] - Indicates if the product is a retail exclusive item.
 * @property {string} selectedColor - The currently selected color for the product.
 * @property {Object} computedLists - Lists computed from the product data.
 * @property {string[]} computedLists.skuList - List of SKUs for the product.
 * @property {Array<{sku: string, size: string, disabled: boolean, lastInStock: boolean, avEcom?: number, sizePos?: string}>} computedLists.sizeList - List of available sizes with their details.
 * @property {string[]} rawImages - Array of raw image URLs.
 * @property {string[]} raw3DModels - Array of raw 3D model URLs.
 * @property {TResponsiveGallery} orderedGallery - The responsive gallery data for the product.
 * @property {string} productPdf - URL to the product PDF documentation.
 * @property {string} brand - The brand name of the product.
 * @property {ColorOption[]} altColorList - List of alternative color options.
 * @property {Object} productDetails - Detailed information about the product.
 * @property {string} productDetails.editoralDescription - Editorial description of the product.
 * @property {string[]} productDetails.technicalDetails - Technical specifications of the product.
 * @property {string} productDetails.SkuCode - The SKU code for the product.
 * @property {string} productDetails.fitDescription - Description of the product's fit.
 * @property {AlgoliaSize} sizes - Size information from Algolia search.
 * @property {string} SizeGridCode - The code for the size grid.
 * @property {extColor[]} extColors - Extended color information.
 * @property {string} [colorHex] - The hexadecimal color code, if available.
 * @property {Array<{label?: string, SKU?: string, Dims?: string, avEcom?: number, avRet?: number, image?: string}>} [extSizes] - Extended size information.
 * @property {boolean} hasExtSizes - Indicates if the product has extended sizes.
 * @property {string} [Dims] - Dimensions information.
 * @property {boolean} [infoCommerce] - Commerce information flag.
 * @property {MainCategory[]} mainCategory - The main product categories.
 * @property {string[]} [editorialDeliveryKeys] - Keys for editorial delivery.
 * @property {string[]} productCategoriesSlugs - Slugs for product categories.
 * @property {string} [productGender] - The gender the product is designed for.
 * @property {Object} categories - Categorization information.
 * @property {string} categories.category1 - Primary category.
 * @property {string} categories.category2 - Secondary category.
 * @property {string} categories.category4 - Quaternary category.
 * @property {ProductCareItemList} productCare - Care instructions for the product.
 * @property {string[]} [editorialProductFocus] - Editorial focus points for the product.
 * @property {PdpEcommerceBlockProductFocusSideSlideProps | null} [productFocus] - Product focus information for side-slide presentation.
 * @property {Object.<string, string>} upcs - Universal Product Codes mapped by size.
 * @property {SizeData[]} sizesData - Detailed size information.
 * @property {string} productPath - The full path to the product page including locale.
 * @property {string} productPathWithoutLocale - The path to the product page without locale.
 * @property {Array<{rel: string, hreflang: string, href: string}>} alternateUrls - List of alternate URLs for different languages.
 * @property {string} MFC - Manufacturer's code.
 * @property {PdpEcommerceBlockProductPdpMessageSideSlideProps | null} pdpMessage - Pdp Message for the product.
 */
export type PDPMappedProductData = {
  title: string
  price: number
  priceCents?: number
  amountFloat: number
  currencyCode: string
  oldPrice?: number
  discount?: string
  tags?: string[]
  isRetailExclusive?: boolean
  selectedColor: string
  computedLists: {
    skuList: string[]
    sizeList: {
      sku: string
      size: string
      disabled: boolean
      lastInStock: boolean
      avEcom?: number | undefined
      sizePos?: string
    }[]
  }
  rawImages: string[]
  raw3DModels: string[]
  orderedGallery: TResponsiveGallery
  productPdf: string
  brand: string
  altColorList: ColorOption[]
  productDetails: {
    editoralDescription: string
    technicalDetails: string[]
    SkuCode: string
    fitDescription: string
  }
  sizes: AlgoliaSize
  SizeGridCode: string
  extColors: extColor[]
  colorHex?: string
  extSizes?: {
    label?: string
    SKU?: string
    Dims?: string
    avEcom?: number
    avRet?: number
    image?: string
  }[]
  hasExtSizes: boolean
  Dims?: string
  infoCommerce?: boolean
  mainCategory: MainCategory[]
  editorialDeliveryKeys?: string[]
  productCategoriesSlugs: string[]
  productGender?: string
  categories: {
    category1: string
    category2: string
    category4: string
  }
  productCare: ProductCareItemList
  editorialProductFocus?: string[]
  pdpMessage?: PdpEcommerceBlockProductPdpMessageSideSlideProps | null
  productFocus?: PdpEcommerceBlockProductFocusSideSlideProps | null
  upcs: { [size: string]: string }
  sizesData: SizeData[]
  productPath: string
  productPathWithoutLocale: string
  alternateUrls: {
    rel: string
    hreflang: string
    href: string
  }[]
  MFC: string
  finalSales?: string
}
type AlternateNameClean = { name: string; lang: string }

/**
 * Composable for handling Product Detail Page (PDP) data fetching and mapping.
 *
 * @returns {Object} An object containing methods and states for PDP data management
 */
export const useTemplatePdp = () => {
  const nuxtApp = useNuxtApp()
  const requestUrl = useRequestURL()
  const configs = useConfigs()
  const appConfig = useAppConfig()
  const algoliaIndex = appConfig.currentAlgoliaIndex
  const { $cl } = useNuxtApp()
  const { lineItems } = useCart()
  const { setCartEventsPayload } = useGACartEvents()
  const { getProductCareItemsByID } = useProductCare()

  const { pdpPath } = useRouteHelper()
  const langCountryMap = getLangCountryMap()

  /**
   * Reactive state for storing mapped product data
   * @type {Ref<PDPMappedProductData | null>}
   */
  const productDataState = useState<PDPMappedProductData | null>(
    'pdp-mapped-product-data',
    () => null
  )

  /**
   * Reactive state for storing product data for Google Analytics
   * @type {Ref<{algoliaProduct: AlgoliaProductType, commercelayerProduct: Sku[]} | null>}
   */
  const productDataStateForGA = useState<{
    algoliaProduct: AlgoliaProductType
    commercelayerProduct: Price[]
  } | null>('product-state-for-ga', () => null)

  // HELPER METHODS
  /**
   * Determines if a SKU is disabled based on stock and availability
   *
   * @param {Sku[] | null} skus - List of SKUs
   * @param {string} [skuCode] - SKU code to check
   * @param {boolean} [isAvailable] - Availability flag
   * @returns {boolean} Whether the SKU is disabled
   */
  const isDisabled = (
    skus?: Sku[] | null,
    skuCode?: string,
    isAvailable?: boolean
  ) => {
    const sku = skus?.find(sku => sku.code === skuCode) ?? false
    return !sku || !isInStock(sku) || !isAvailable
  }

  /**
   * Checks if a SKU is the last one in stock
   *
   * @param {boolean} isTheLastOne - Flag indicating if it's the last item
   * @param {Sku[] | null} skus - List of SKUs
   * @param {string} [skuCode] - SKU code to check
   * @returns {boolean} Whether the SKU is the last in stock
   */
  const isLastInStock = (
    isTheLastOne: boolean,
    skus?: Sku[] | null,
    skuCode?: string
  ) => {
    const sku = skus?.find(sku => sku.code === skuCode) ?? false
    return !sku || isLastInStockFn(sku) || isTheLastOne
  }

  /**
   * Retrieves UPCs (Universal Product Codes) for given SKUs
   *
   * @param {Sku[] | null} clPricesAndStocks - List of SKUs with prices and stocks
   * @returns {Object} Object mapping sizes to their UPCs
   */
  const getUpcs = (clPricesAndStocks: Sku[] | null) => {
    const upcs: { [size: string]: string } = {}
    if (clPricesAndStocks) {
      const clPricesAndStocksVal = clPricesAndStocks
      clPricesAndStocksVal.forEach(clPricesAndStock => {
        const productCode = clPricesAndStock.code
        const codeSplit = productCode.split('_') ?? []
        const getSize = codeSplit.filter((item, index) => index > 3)
        const size: string = getSize.join('_')
        upcs[size] = clPricesAndStock.reference as string
      })
    }
    return upcs
  }

  /**
   * Computes lists of SKUs and sizes with their availability and stock status
   *
   * @param {AlgoliaProductType['size']} sizes - Product sizes from Algolia
   * @param {Sku[]} clPricesAndStocks - SKUs with prices and stocks from Commerce Layer
   * @returns {Object} Object containing SKU list and size list with their properties
   */
  const getComputedLists = (
    sizes: AlgoliaProductType['size'],
    clPricesAndStocks: Sku[]
  ) => {
    const skuList: string[] = []
    const sizeList: {
      sku: string
      size: string
      disabled: boolean
      lastInStock: boolean
      avEcom?: number
      sizePos?: string
    }[] = []

    sizes?.forEach(size => {
      const itemInCart = lineItems.value.find(
        item => item.sku_code! === size.SKU
      )
      if (typeof size.SKU === 'string' && !skuList.includes(size.SKU)) {
        skuList.push(size.SKU)
      }
      if (
        typeof size.label === 'string' &&
        typeof size.SKU === 'string' &&
        !sizeList.some(item => item.sku === size.SKU)
      ) {
        sizeList.push({
          sku: size.SKU,
          size: size.label,
          sizePos: size.sizePos,
          disabled: isDisabled(
            clPricesAndStocks,
            size.SKU,
            itemInCart
              ? itemInCart.quantity < (itemInCart.item.inventory?.quantity || 0)
              : true
          ),
          lastInStock: isLastInStock(
            itemInCart
              ? itemInCart.item.inventory?.quantity - itemInCart?.quantity === 1
              : false,
            clPricesAndStocks,
            size.SKU
          ),
        })
      }
    })

    return {
      skuList,
      sizeList,
    }
  }

  /**
   * Retrieves alternative color list for a product
   *
   * @param {Object} params - Parameters for fetching alternative colors
   * @param {AlgoliaProductType['alternative-color']} params.altColors - Alternative colors
   * @param {string[]} params.computedImages - Computed images
   * @param {string} params.selectedColor - Currently selected color
   * @param {AlgoliaProductType['MacroColorEsa']} params.colorHex - Color hex code
   * @param {boolean} params.isInStockSelectedColor - Whether the selected color is in stock
   * @param {extColor[]} params.extColors - Extended colors
   * @param {string} params.skuCode - SKU code
   * @param {string} params.currentProductPath - Current product path
   * @returns {Promise<ColorOption[]>} List of alternative color options
   */
  const getAltColorList = async (params: {
    altColors: AlgoliaProductType['alternative-color']
    computedImages: string[]
    selectedColor: string
    colorHex: AlgoliaProductType['MacroColorEsa']
    isInStockSelectedColor: boolean
    extColors: extColor[]
    skuCode: string
    currentProductPath: string
  }) => {
    const {
      altColors,
      computedImages,
      selectedColor,
      colorHex,
      isInStockSelectedColor,
      extColors,
      skuCode,
      currentProductPath,
    } = params

    let mappedAltColorsList: ColorOption[] = []

    // if the product as altColors fetch the data for each colorSku and map it to the ColorOption type
    if (altColors) {
      mappedAltColorsList = (
        await Promise.all(
          altColors.map(async element => {
            const altProduct = await nuxtApp.runWithContext(
              async () =>
                await $fetch(
                  `/api/getProduct/${algoliaIndex}/${element.objectID}`
                ).catch(() => null)
            )
            if (!altProduct) return
            // keep assets, not images
            const altImage = getFirstMediaByPriority(checkMedia(altProduct))
            const altColor = altProduct?.ColorDesc

            const isInStockAltColor = altProduct.size.some(
              size => size.avEcom && size.avEcom > 0
            )

            return {
              thumbnailUrl: altImage?.split('/')?.slice(-1)?.toString() ?? '',
              color: altColor,
              sku: altProduct.objectID,
              isInStock: isInStockAltColor,
              path: pdpPath(altProduct),
            } as ColorOption
          })
        )
      ).filter((colorOpt: ColorOption | undefined) => !!colorOpt)

      mappedAltColorsList.unshift({
        thumbnailUrl: computedImages[0] ?? '',
        color: selectedColor,
        sku: skuCode,
        isInStock: isInStockSelectedColor,
        path: currentProductPath,
      })
    }

    // if the product has extColors (i.e. colors for beauty products, such as lipstick), fetch the data for each colorSku and map it to the ColorOption type
    if (extColors?.length > 0) {
      const mappedExtColors = (
        await Promise.all(
          extColors.map(async extElement => {
            // fetch algolia data to build the extColor product path
            const extColorProduct = await nuxtApp.runWithContext(
              async () =>
                await $fetch(
                  `/api/getProduct/${algoliaIndex}/${extElement.objectID}`
                ).catch(() => null)
            )
            if (!extColorProduct) return

            return {
              thumbnailUrl: '',
              hexColor: extElement.hexColor,
              color: extElement.label,
              sku: extElement.objectID,
              isInStock: isInStockSelectedColor,
              path: pdpPath(extColorProduct),
            } as ColorOption
          })
        )
      ).filter((colorOpt: ColorOption | undefined) => !!colorOpt)

      mappedAltColorsList = [
        {
          thumbnailUrl: '',
          hexColor: colorHex ? colorHex : '',
          color: selectedColor,
          sku: skuCode,
          isInStock: isInStockSelectedColor,
          path: currentProductPath,
        },
        ...mappedExtColors,
      ]
    }

    return mappedAltColorsList
  }

  const getSizesData = async (
    items: AlgoliaProductType['size'],
    sizeTable?: string
  ) => {
    if (!items || items.length === 0) return []
    const gridCodes = items.map(item => item?.sizeCode)

    const data = await $fetch<Record<string, SizeData>>(
      '/api/getSizesBySizeFile/getSizesByCode',
      {
        method: 'POST',
        body: { sizeCodes: gridCodes, sizeTable },
      }
    )
    const sizes = Object.values(data)
    return sizes as SizeData[]
  }

  // FETCH
  /**
   * Fetches a product from Algolia by its SKU
   *
   * @param {string} product - Product SKU
   * @returns {Promise<AlgoliaProductType>} Fetched Algolia product
   * @throws {Error} If product is not found
   */
  const fetchAlgoliaProduct = async (product: string) => {
    const algoliaProduct = await $fetch(
      `/api/getProduct/${algoliaIndex}/${product}`
    )
    if (!algoliaProduct) {
      throw createError({
        statusCode: 404,
        message: `[ERROR]: PRODUCT ${product} NOT FOUND`,
        statusMessage: `[ERROR]: PRODUCT ${product} NOT FOUND`,
      })
    }

    setCartEventsPayload({ algoliaObjects: [algoliaProduct] })

    return algoliaProduct as AlgoliaProductType
  }

  /**
   * Fetches prices and stocks for given SKUs from Commerce Layer
   *
   * @param {string[]} skuList - List of SKU codes
   * @returns {Promise<Sku[]>} List of SKUs with prices and stocks
   * @throws {Error} If SKUs are not found or fetch fails
   */
  const fetchCLPricesAndStocks = async (skuList: string[]) => {
    if (skuList.length < 0)
      throw createError({
        statusCode: 404,
        message: `[ERROR] FETCH CL PRICES AND STOCK: NOT FOUND THESE SKUS: ${skuList.join(', ')}`,
        statusMessage: `[ERROR] FETCH CL PRICES AND STOCK: NOT FOUND THESE SKUS: ${skuList.join(', ')}`,
      })
    try {
      const clPricesAndStocks = skuList.length
        ? (
            await Promise.all(
              chunk(skuList, 25).map(skuCodesChunk =>
                $cl.prices.list({
                  include: [
                    'sku',
                    'sku.stock_items',
                    'sku.stock_items.reserved_stock',
                  ],
                  filters: {
                    sku_code_in: skuCodesChunk?.join() ?? '',
                  },
                  pageSize: 25,
                })
              )
            )
          ).flat()
        : []
      if (!clPricesAndStocks) {
        throw createError({
          statusCode: 404,
          statusMessage: `[ERROR] FETCH CL PRICES AND STOCK: NOT FOUND DATA FROM COMMERCELAYER OF THESE SKUS: ${skuList.join(', ')}`,
          message: `[ERROR] FETCH CL PRICES AND STOCK: NOT FOUND DATA FROM COMMERCELAYER OF THESE SKUS: ${skuList.join(', ')}`,
        })
      }
      return clPricesAndStocks
    } catch (error) {
      console.error(
        `[ERROR] FETCH CL PRICES AND STOCK: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
      throw error
    }
  }

  /**
   * Maps Algolia product data to a structured format
   *
   * @param {AlgoliaProductType} algoliaProduct - Algolia product data
   * @returns {Promise<PDPMappedProductData>} Mapped product data
   */
  const mapAlgoliaProductData = async (algoliaProduct: AlgoliaProductType) => {
    const title = algoliaProduct.Name

    const tags = algoliaProduct.tags

    const selectedColor =
      algoliaProduct?.ColorDesc! ??
      algoliaProduct?.MacroColorDesc?.split('_')?.[1] ??
      ''

    const sizes = algoliaProduct.size.sort(
      (a, b) => parseInt(a.sizePos ?? '0') - parseInt(b.sizePos ?? '0')
    )

    const extColors = algoliaProduct['ext-color'] as extColor[]

    const colorHex = algoliaProduct.MacroColorEsa

    const extSizes = algoliaProduct['ext-size']
    const Dims = algoliaProduct.Dims

    const infoCommerce = algoliaProduct.infoCommerce
    const algoliaAssets = algoliaProduct.assets

    const rawImages = getRawImages(algoliaAssets)
    const raw3DModels = get3DModelURLs(algoliaAssets)
    const computedImages = getFormattedImages(algoliaAssets)
    const orderedGallery = getOrderedGallery(algoliaAssets)
    const productPdf =
      algoliaAssets?.find(asset => asset.type === 'pdf')?.URL ?? ''

    const brand = algoliaProduct.Brand ?? ''

    const altColors = algoliaProduct['alternative-color'] ?? []
    const alternateNames =
      algoliaProduct.NameURL?.filter(
        (nu): nu is AlternateNameClean => !!nu.lang && !!nu.name
      ) ?? []

    const langNameMap = new Map(alternateNames.map(an => [an.lang, an.name]))

    const alternateUrls = langCountryMap
      .filter(lc => langNameMap.has(lc.split('-')[0]))
      .map(lc => {
        const [lang] = lc.split('-')
        const thisLangPdpUrl = algoliaProduct.NameURL
          ? getPdpPath(algoliaProduct, lang)
          : ''
        return {
          rel: 'alternate',
          hreflang: lc,
          href: requestUrl.origin + '/' + lc + thisLangPdpUrl,
        }
      })

    const xDefault = alternateUrls?.find(au => au.hreflang === 'en-wx')
    if (xDefault) alternateUrls.push({ ...xDefault, hreflang: 'x-default' })

    // TODO: (tracked #1200) waiting for client feedback
    const mainCategory = algoliaProduct?.categoryPageId

    const editorialDeliveryKeys = algoliaProduct?.editorial

    const productCategories = algoliaProduct.categoryPageId

    const productPath = pdpPath(algoliaProduct)
    const productPathWithoutLocale = pdpPath(algoliaProduct, true, false)

    const isRetailExclusive = !!algoliaProduct.attributes?.RetailExclusive

    const productCategoriesSlugs = productCategories
      .map(categories => categories.slug)
      .filter(isNonNullable)

    const productGender = algoliaProduct.Gender
    const categories = extractCategoriesUnlocalized(
      algoliaProduct?.hierarchicalCategories || [],
      algoliaProduct?.categoryPageId2 || []
    )
    const isInStockSelectedColor = sizes.some(
      size => size.avEcom && size.avEcom > 0
    )

    // ALT COLORS
    // many times are empty
    const altColorList = await getAltColorList({
      altColors,
      computedImages,
      selectedColor,
      isInStockSelectedColor,
      extColors,
      colorHex,
      skuCode: algoliaProduct.objectID,
      currentProductPath: productPath,
    })

    const productDetails = {
      composition: algoliaProduct?.Composition!,
      editoralDescription: algoliaProduct?.LongDescription!,
      technicalDetails: [algoliaProduct?.ShortDescription!],
      SkuCode: algoliaProduct?.MFC!,
      fitDescription: '',
      // algoliaProduct?.modelFit ??
    }

    const productCare = getProductCareItemsByID(algoliaProduct.care ?? [])
    const { data: productFocus } = await nuxtApp.runWithContext(
      async () => await useProductFocus(algoliaProduct.focus ?? [])
    )
    const hasExtSizes = !!extSizes && extSizes?.length > 0
    const sizeTable = algoliaProduct.sizeTable
    const sizesData: SizeData[] = await getSizesData(sizes, sizeTable)

    const SizeGridCode = sizesData?.[0]?.SizeGrid_Code_Category

    const editorialProductFocus =
      algoliaProduct['editorial-product-focus'] ?? []

    let pdpMessage: PdpEcommerceBlockProductPdpMessageSideSlideProps | null =
      null
    if (
      algoliaProduct.pdp_message &&
      algoliaProduct.pdp_message.trim() !== ''
    ) {
      const { productPdpMessage } = await nuxtApp.runWithContext(
        async () => await useProductPdpMessage(algoliaProduct.pdp_message!)
      )
      pdpMessage = productPdpMessage.value || null
    }

    return {
      title,
      tags,
      isRetailExclusive,
      selectedColor,
      rawImages,
      raw3DModels,
      orderedGallery,
      productPdf,
      brand,
      altColorList,
      productDetails,
      sizes,
      extColors,
      colorHex,
      extSizes,
      hasExtSizes,
      mainCategory,
      editorialDeliveryKeys,
      productCare,
      editorialProductFocus,
      productFocus: productFocus.value,
      productCategoriesSlugs,
      productGender,
      categories,
      sizesData,
      Dims,
      SizeGridCode,
      productPath,
      productPathWithoutLocale,
      alternateUrls,
      infoCommerce,
      finalSales: algoliaProduct.FinalSales,
      MFC: algoliaProduct.MFC,
      pdpMessage,
    }
  }

  /**
   * Maps Commerce Layer product data to a structured format
   *
   * @param {Price[]} clPricesAndStocks - Prices with SKUs and stocks
   * @param {AlgoliaProductType} algoliaProduct - Algolia product data
   * @returns {Object} Mapped Commerce Layer product data
   */
  const mapCLProductData = (
    clPricesAndStocks: Price[],
    algoliaProduct: AlgoliaProductType
  ) => {
    const skuPrice = clPricesAndStocks?.[0]
    const country = countries.find(
      country => country.code === configs.value.country
    )
    const currencyCode =
      skuPrice?.currency_code ?? (country && country.cl.currency) ?? 'EUR'

    const amountFloat = skuPrice?.amount_float ?? algoliaProduct.FullPrice

    const priceCents = skuPrice?.amount_cents

    const price: number = amountFloat

    const oldPrice =
      skuPrice?.compare_at_amount_float! > amountFloat
        ? skuPrice?.compare_at_amount_float!
        : undefined

    const discount =
      skuPrice?.compare_at_amount_float! > amountFloat
        ? percentageDiscount(skuPrice?.compare_at_amount_float!, amountFloat)
        : undefined

    // COMPUTED LISTS
    const sizes = algoliaProduct.size.sort(
      (a, b) => parseInt(a.sizePos ?? '0') - parseInt(b.sizePos ?? '0')
    )
    const skuList = clPricesAndStocks.map(price => price.sku).filter(v => !!v)

    const computedLists = getComputedLists(sizes, skuList)

    const upcs = getUpcs(skuList)

    return {
      price,
      priceCents,
      amountFloat,
      currencyCode,
      oldPrice,
      discount,
      computedLists,
      upcs,
    }
  }

  /**
   * Fetches and maps product data from both Algolia and Commerce Layer
   *
   * @param {string} code - Product SKU code
   * @param {Object} [options] - Optional configuration
   * @param {boolean} [options.updateState=false] - Whether to update reactive states
   * @returns {Promise<PDPMappedProductData | null>} Mapped product data or null
   */
  const fetchAndMapProductData = async (
    code: string,
    options?: { updateState?: boolean }
  ) => {
    // on server, fetch algolia data and cache it
    // on client, use the cached data and fetch updated cl data, unless the data refresh is triggered by ext size change

    const algoliaP = await fetchAlgoliaProduct(code)

    if (!algoliaP) {
      console.error('Error fetching product data for extSize SKU:', code)
      return null
    }

    const productSizesSkuList = algoliaP.size
      ?.map(item => item.SKU)
      .filter(skuCode => skuCode !== undefined)

    const clStock = await fetchCLPricesAndStocks(productSizesSkuList)

    if (!clStock) {
      console.error('Error fetching product data for extSize SKU:', code)
      return null
    }

    if (options?.updateState) {
      productDataStateForGA.value = {
        algoliaProduct: algoliaP,
        commercelayerProduct: clStock,
      }
    }

    const algoliaDataMapped = await mapAlgoliaProductData(algoliaP)

    const clDataMapped = await mapCLProductData(clStock, algoliaP)

    const productData = {
      ...algoliaDataMapped,
      ...clDataMapped,
    }

    if (options?.updateState) productDataState.value = productData

    return productData
  }

  return {
    fetchAndMapProductData,
    productDataState, // THIS IS THE STATE YOU WANT TO IMPORT IF YOU NEED REACTIVE PRODUCT DATA
  }
}
